import React from 'react';
import { transitionTheme } from '@/utils/themeTransition';
import { Sun, Moon, LockKey } from '@phosphor-icons/react';
import ChangePasswordModal from '@/components/ChangePasswordModal';

// Full theme toggle for profile pages
const ThemeToggle = ({ accent = '#10b981' }) => {
  const [theme, setTheme] = React.useState(() => {
    return localStorage.getItem('theme') || 'light';
  });
  const [showPwModal, setShowPwModal] = React.useState(false);
  React.useEffect(() => {
    const observer = new MutationObserver(() => setTheme(document.documentElement.getAttribute('data-theme') || 'light'));
    observer.observe(document.documentElement, { attributes:true, attributeFilter:['data-theme'] });
    return () => observer.disconnect();
  }, []);


  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  return (
    <>
      <div className="bg-surface border border-border rounded-[10px] p-6" data-testid="theme-toggle-section">
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-1">Platform</h3>
        <p className="text-[12px] text-text-muted mb-4">Appearance & account settings</p>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[14px] text-text-primary font-medium">Theme</div>
            <div className="text-[12px] text-text-secondary">Switch between light and dark mode</div>
          </div>
          <div className="flex items-center gap-1 bg-background border border-border rounded-lg p-1" data-testid="theme-switch">
            <button onClick={(event) => transitionTheme('light', event, setTheme)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium transition-all ${theme === 'light' ? 'bg-surface text-text-primary shadow-sm' : 'text-text-muted hover:text-text-secondary'}`} data-testid="theme-light-btn">
              <Sun size={14} weight="bold" /> Light
            </button>
            <button onClick={(event) => transitionTheme('dark', event, setTheme)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium transition-all ${theme === 'dark' ? 'bg-surface text-text-primary shadow-sm' : 'text-text-muted hover:text-text-secondary'}`} data-testid="theme-dark-btn">
              <Moon size={14} weight="bold" /> Dark
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <div>
            <div className="text-[14px] text-text-primary font-medium">Password</div>
            <div className="text-[12px] text-text-secondary">Update the password for your account</div>
          </div>
          <button
            type="button"
            onClick={() => setShowPwModal(true)}
            data-testid="change-password-btn"
            className="inline-flex items-center gap-2 px-4 py-2 text-[13px] font-medium text-white rounded-lg transition-colors"
            style={{ backgroundColor: accent }}
          >
            <LockKey size={14} weight="bold" /> Change password
          </button>
        </div>
      </div>
      {showPwModal && (
        <ChangePasswordModal accent={accent} onClose={() => setShowPwModal(false)} />
      )}
    </>
  );
};

// Compact sidebar toggle. Expanded (docked/hovered rail): a two-segment
// sun/moon pill — the active side gets a raised light "thumb" behind it, both
// states visible at a glance. Collapsed (48px rail): a single icon button
// showing the CURRENT theme, since a two-up pill doesn't fit that width.
export const ThemeToggleCompact = ({ expanded = false }) => {
  React.useEffect(() => {
    const observer = new MutationObserver(() => setTheme(document.documentElement.getAttribute('data-theme') || 'light'));
    observer.observe(document.documentElement, { attributes:true, attributeFilter:['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const [theme, setTheme] = React.useState(() => {
    return localStorage.getItem('theme') || 'light';
  });

  const setThemeAndPersist = (next, event) => transitionTheme(next, event, setTheme);

  React.useEffect(() => {
    const stored = localStorage.getItem('theme') || 'light';
    if (stored !== theme) {
      setTheme(stored);
      document.documentElement.setAttribute('data-theme', stored);
    }
    // Mount-only: syncs from localStorage in case another tab/component changed
    // it before this one mounted. Re-running on every `theme` change would be
    // harmless (localStorage is already current by then) but pointless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={(event) => setThemeAndPersist(document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark', event)}
        className="w-full flex items-center justify-center p-2 rounded-lg text-text-muted hover:text-text-primary transition-colors"
        data-testid="sidebar-theme-toggle"
        title={theme === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
      >
        {theme === 'dark' ? <Sun size={20} weight="bold" /> : <Moon size={20} weight="bold" />}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-0.5 border rounded-full p-0.5" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)' }} data-testid="sidebar-theme-toggle">
      <button
        type="button"
        onClick={(event) => setThemeAndPersist('light', event)}
        className={`flex flex-1 items-center justify-center gap-1 py-1.5 rounded-full text-xs font-medium transition-all ${
          theme === 'light' ? 'shadow-sm' : ''
        }`}
        data-testid="sidebar-theme-light-btn"
        style={{ backgroundColor: theme === 'light' ? 'var(--card)' : 'transparent', color: theme === 'light' ? 'var(--foreground)' : 'var(--muted-foreground)' }}
        title="Switch to Light"
      >
        <Sun size={12} weight={theme === 'light' ? 'fill' : 'bold'} /> Light
      </button>
      <button
        type="button"
        onClick={(event) => setThemeAndPersist('dark', event)}
        className={`flex flex-1 items-center justify-center gap-1 py-1.5 rounded-full text-xs font-medium transition-all ${
          theme === 'dark' ? 'shadow-sm' : ''
        }`}
        data-testid="sidebar-theme-dark-btn"
        style={{ backgroundColor: theme === 'dark' ? 'var(--card)' : 'transparent', color: theme === 'dark' ? 'var(--foreground)' : 'var(--muted-foreground)' }}
        title="Switch to Dark"
      >
        <Moon size={12} weight={theme === 'dark' ? 'fill' : 'bold'} /> Dark
      </button>
    </div>
  );
};

export default ThemeToggle;
